import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, PlusCircle, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectPICProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  maxDisplay?: number;
  disabled?: boolean;
}

export function MultiSelectPIC({
  value = [],
  onChange,
  options,
  maxDisplay = 1,
  disabled = false
}: MultiSelectPICProps) {
  const [open, setOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<string[]>(value);

  // Sync selected to tempSelected when selected changes from outside
  React.useEffect(() => {
    setTempSelected(Array.isArray(value) ? [...value] : []);
  }, [value]);

  // Handle toggling individual items
  const toggleOption = (option: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    setTempSelected(prev => {
      if (prev.includes(option)) {
        return prev.filter(val => val !== option);
      } else {
        return [...prev, option];
      }
    });
  };
  
  // Handle removing items directly from the badge
  const removeOption = (option: string) => {
    if (disabled) return;
    onChange(value.filter(val => val !== option));
  };

  // Save changes
  const handleSave = () => {
    if (disabled) return;
    onChange(tempSelected);
    setOpen(false);
  };

  // Cancel changes
  const handleCancel = () => {
    setTempSelected([...value]);
    setOpen(false);
  };

  // Get option label from value
  const getOptionLabel = (optionValue: string) => {
    const option = options.find(opt => opt.value === optionValue);
    return option ? option.label : optionValue;
  };

  // Calculate how many items to display directly and how many to hide in "+X more" label
  const displayItems = value.slice(0, maxDisplay);
  const remainingCount = value.length > maxDisplay ? value.length - maxDisplay : 0;
  
  return (
    <div className="flex flex-wrap gap-1">
      {displayItems.map((item, i) => (
        <Badge 
          key={i} 
          variant="secondary"
          className="flex items-center gap-1 px-2 py-1 h-6 bg-blue-50 text-blue-700 border border-blue-200"
        >
          {getOptionLabel(item)}
          {!disabled && (
            <X 
              className="h-3 w-3 cursor-pointer hover:text-destructive" 
              onClick={() => removeOption(item)}
            />
          )}
        </Badge>
      ))}
      
      {remainingCount > 0 && (
        <Badge variant="outline" className="px-2 py-1 h-6">
          +{remainingCount} more
        </Badge>
      )}
      
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-6 px-2 ml-1"
            disabled={disabled}
          >
            <PlusCircle className="h-3 w-3 mr-1" />
            Edit
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-56 p-0 z-[99999]" 
          align="start"
          sideOffset={5}
          avoidCollisions={true}
          style={{
            backgroundColor: 'white',
            color: '#333',
            pointerEvents: 'auto',
            zIndex: 99999
          }}
        >
          <div className="p-3 border-b border-gray-200 bg-white">
            <h4 className="text-sm font-medium text-gray-800">PICs</h4>
            <p className="text-xs text-gray-500">Choose PICs for this KOL</p>
          </div>
          <ScrollArea className="h-72">
            <div className="p-2">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={(e) => toggleOption(option.value, e)}
                  className={cn(
                    "flex items-center justify-between rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50/50 transition-colors",
                    tempSelected.includes(option.value) ? "bg-blue-50/50" : "",
                    disabled ? "opacity-50 cursor-not-allowed" : ""
                  )}
                  style={{ pointerEvents: disabled ? 'none' : 'auto' }}
                >
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      checked={tempSelected.includes(option.value)}
                      onCheckedChange={() => {
                        if (disabled) return;
                        setTempSelected(prev => {
                          if (prev.includes(option.value)) {
                            return prev.filter(val => val !== option.value);
                          } else {
                            return [...prev, option.value];
                          }
                        });
                      }}
                      id={`pic-option-${option.value}`}
                      className="data-[state=checked]:bg-[#1E88E5]"
                      onClick={(e) => e.stopPropagation()}
                      style={{ pointerEvents: 'auto' }}
                      disabled={disabled}
                    />
                    <label 
                      htmlFor={`pic-option-${option.value}`}
                      className="text-sm cursor-pointer flex-grow text-gray-700"
                    >
                      {option.label}
                    </label>
                  </div>
                  {tempSelected.includes(option.value) && (
                    <Check className="h-4 w-4 ml-auto text-[#1E88E5]" />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="flex justify-end gap-2 p-2 border-t border-gray-200">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCancel}
              className="bg-gray-100 hover:bg-gray-200 border-gray-300"
              style={{ pointerEvents: 'auto' }}
              disabled={disabled}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleSave}
              className="bg-[#1E88E5] hover:bg-[#1565C0]"
              style={{ pointerEvents: 'auto' }}
              disabled={disabled}
            >
              <Check className="h-4 w-4 mr-1" />
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default MultiSelectPIC;